import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import SlideViewer from '../components/SlideViewer';
import { 
    Plus, 
    Edit, 
    Trash2, 
    Layers, 
    BookOpen, 
    ChevronRight,
    Target,
    FileText,
    Lightbulb,
    CheckCircle,
    AlertCircle,
    Clock,
    Play,
    GraduationCap,
    Presentation,
    HelpCircle,
    Search,
    Grid3X3,
    List,
    Eye,
    X,
    MonitorPlay
} from 'lucide-react';

const Modules = () => {
    const { api, user } = useAuth();
    const [modules, setModules] = useState([]);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        topic_ids: []
    });
    
    // View states
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedModule, setSelectedModule] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [topicDetailOpen, setTopicDetailOpen] = useState(false);
    
    // Slide viewer states
    const [slideViewerOpen, setSlideViewerOpen] = useState(false);
    const [slideViewerData, setSlideViewerData] = useState({ slides: [], title: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [modulesRes, topicsRes] = await Promise.all([
                api.get('/modules'),
                api.get('/topics')
            ]);
            setModules(modulesRes.data);
            setTopics(topicsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingModule) {
                await api.put(`/modules/${editingModule.id}`, formData);
                toast.success('Module updated');
            } else {
                await api.post('/modules', formData);
                toast.success('Module created');
            }
            fetchData();
            resetForm();
        } catch (error) {
            toast.error('Failed to save module');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this module?')) return;
        try {
            await api.delete(`/modules/${id}`);
            toast.success('Module deleted');
            if (selectedModule?.id === id) setSelectedModule(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete module');
        }
    };

    const toggleTopic = (topicId) => {
        setFormData(prev => ({
            ...prev,
            topic_ids: prev.topic_ids.includes(topicId)
                ? prev.topic_ids.filter(id => id !== topicId)
                : [...prev.topic_ids, topicId]
        }));
    };

    const resetForm = () => {
        setFormData({ name: '', description: '', topic_ids: [] });
        setEditingModule(null);
        setDialogOpen(false);
    };

    const openEdit = (module) => {
        setEditingModule(module);
        setFormData({
            name: module.name,
            description: module.description,
            topic_ids: module.topic_ids || []
        });
        setDialogOpen(true);
    };

    const getTopicsForModule = (module) => {
        return topics.filter(t => module.topic_ids?.includes(t.id));
    };

    const openTopicDetail = (topic) => {
        setSelectedTopic(topic);
        setTopicDetailOpen(true);
    };

    // Filter modules based on search
    const filteredModules = modules.filter(m => 
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isAdmin = user?.role === 'admin' || user?.role === 'trainer';

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="modules-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Layers className="h-8 w-8 text-primary" />
                            Learning Modules
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Explore all modules and their topics with detailed content
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search modules..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 w-[200px]"
                                data-testid="search-modules"
                            />
                        </div>
                        
                        {/* View toggle */}
                        <div className="flex items-center border rounded-lg p-1">
                            <Button
                                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                className="h-8 w-8 p-0"
                            >
                                <Grid3X3 className="h-4 w-4" />
                            </Button>
                            <Button
                                variant={viewMode === 'list' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('list')}
                                className="h-8 w-8 p-0"
                            >
                                <List className="h-4 w-4" />
                            </Button>
                        </div>
                        
                        {/* Create button (admin only) */}
                        {isAdmin && (
                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button className="btn-primary" data-testid="create-module-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create Module
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg">
                                    <DialogHeader>
                                        <DialogTitle>{editingModule ? 'Edit Module' : 'Create Module'}</DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Module Name</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                required
                                                data-testid="module-name-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="description">Description</Label>
                                            <Textarea
                                                id="description"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                required
                                                data-testid="module-description-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Select Topics</Label>
                                            <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                                                {topics.map((topic) => (
                                                    <div key={topic.id} className="flex items-center gap-2">
                                                        <Checkbox
                                                            id={topic.id}
                                                            checked={formData.topic_ids.includes(topic.id)}
                                                            onCheckedChange={() => toggleTopic(topic.id)}
                                                        />
                                                        <label htmlFor={topic.id} className="text-sm cursor-pointer">
                                                            {topic.name}
                                                        </label>
                                                    </div>
                                                ))}
                                                {topics.length === 0 && (
                                                    <p className="text-sm text-muted-foreground">No topics available</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 pt-4">
                                            <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                                                Cancel
                                            </Button>
                                            <Button type="submit" className="flex-1" data-testid="save-module-btn">
                                                {editingModule ? 'Update' : 'Create'}
                                            </Button>
                                        </div>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Modules List */}
                    <div className={viewMode === 'grid' ? 'lg:col-span-2' : 'lg:col-span-1'}>
                        {viewMode === 'grid' ? (
                            <div className="grid gap-4 md:grid-cols-2">
                                {filteredModules.map((module) => {
                                    const moduleTopics = getTopicsForModule(module);
                                    const approvedCount = moduleTopics.filter(t => t.status === 'approved').length;
                                    
                                    return (
                                        <Card 
                                            key={module.id}
                                            className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${
                                                selectedModule?.id === module.id ? 'border-primary shadow-lg' : ''
                                            }`}
                                            onClick={() => setSelectedModule(module)}
                                            data-testid={`module-card-${module.id}`}
                                        >
                                            <CardHeader className="pb-3">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20">
                                                            <Layers className="h-5 w-5 text-primary" />
                                                        </div>
                                                        <div>
                                                            <CardTitle className="text-lg">{module.name}</CardTitle>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <Badge variant="secondary" className="text-xs">
                                                                    <BookOpen className="h-3 w-3 mr-1" />
                                                                    {moduleTopics.length} topics
                                                                </Badge>
                                                                {approvedCount > 0 && (
                                                                    <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">
                                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                                        {approvedCount} ready
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isAdmin && (
                                                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(module)}>
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(module.id)}>
                                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                                                    {module.description}
                                                </p>
                                                {moduleTopics.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {moduleTopics.slice(0, 3).map(topic => (
                                                            <Badge key={topic.id} variant="outline" className="text-xs">
                                                                {topic.name}
                                                            </Badge>
                                                        ))}
                                                        {moduleTopics.length > 3 && (
                                                            <Badge variant="outline" className="text-xs">
                                                                +{moduleTopics.length - 3} more
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {filteredModules.length === 0 && (
                                    <Card className="col-span-2">
                                        <CardContent className="flex flex-col items-center justify-center py-12">
                                            <Layers className="h-12 w-12 text-muted-foreground mb-4" />
                                            <p className="text-muted-foreground">No modules found</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <Card>
                                <CardContent className="p-0">
                                    <ScrollArea className="h-[600px]">
                                        {filteredModules.map((module, idx) => {
                                            const moduleTopics = getTopicsForModule(module);
                                            return (
                                                <div
                                                    key={module.id}
                                                    className={`p-4 border-b cursor-pointer transition-colors hover:bg-accent/50 ${
                                                        selectedModule?.id === module.id ? 'bg-accent' : ''
                                                    }`}
                                                    onClick={() => setSelectedModule(module)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 rounded-lg bg-primary/10">
                                                                <Layers className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium">{module.name}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {moduleTopics.length} topics
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Module Detail / Topics Panel */}
                    <div className={viewMode === 'grid' ? 'lg:col-span-1' : 'lg:col-span-2'}>
                        {selectedModule ? (
                            <Card className="sticky top-4">
                                <CardHeader className="border-b">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <Layers className="h-5 w-5 text-primary" />
                                                {selectedModule.name}
                                            </CardTitle>
                                            <CardDescription className="mt-1">
                                                {selectedModule.description}
                                            </CardDescription>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="icon"
                                            onClick={() => setSelectedModule(null)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <ScrollArea className="h-[500px]">
                                        <div className="p-4">
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <BookOpen className="h-4 w-4 text-primary" />
                                                Topics in this Module
                                            </h4>
                                            <Accordion type="single" collapsible className="space-y-2">
                                                {getTopicsForModule(selectedModule).map((topic, idx) => (
                                                    <AccordionItem 
                                                        key={topic.id} 
                                                        value={topic.id}
                                                        className="border rounded-lg px-4"
                                                    >
                                                        <AccordionTrigger className="hover:no-underline py-3">
                                                            <div className="flex items-center gap-3 text-left">
                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                                    topic.status === 'approved' 
                                                                        ? 'bg-emerald-500/10 text-emerald-500' 
                                                                        : 'bg-muted text-muted-foreground'
                                                                }`}>
                                                                    {idx + 1}
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium">{topic.name}</p>
                                                                    <div className="flex items-center gap-2 mt-0.5">
                                                                        <Badge 
                                                                            variant={topic.status === 'approved' ? 'default' : 'secondary'}
                                                                            className="text-xs"
                                                                        >
                                                                            {topic.status}
                                                                        </Badge>
                                                                        {topic.sections?.length > 0 && (
                                                                            <span className="text-xs text-muted-foreground">
                                                                                {topic.sections.length} sections
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </AccordionTrigger>
                                                        <AccordionContent className="pt-2 pb-4">
                                                            <div className="space-y-4">
                                                                {topic.description && (
                                                                    <p className="text-sm text-muted-foreground">
                                                                        {topic.description}
                                                                    </p>
                                                                )}
                                                                
                                                                {/* Learning Objectives */}
                                                                {topic.learning_objectives?.length > 0 && (
                                                                    <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                                                        <h5 className="text-sm font-medium flex items-center gap-2 mb-2 text-emerald-600">
                                                                            <Target className="h-4 w-4" />
                                                                            Learning Objectives
                                                                        </h5>
                                                                        <ul className="space-y-1">
                                                                            {topic.learning_objectives.map((obj, i) => (
                                                                                <li key={i} className="text-xs flex items-start gap-2">
                                                                                    <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                                                                                    {obj}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Quick Stats */}
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    <div className="p-2 rounded-lg bg-muted/50 text-center">
                                                                        <FileText className="h-4 w-4 mx-auto mb-1 text-blue-500" />
                                                                        <p className="text-xs font-medium">{topic.sections?.length || 0}</p>
                                                                        <p className="text-xs text-muted-foreground">Sections</p>
                                                                    </div>
                                                                    <div className="p-2 rounded-lg bg-muted/50 text-center">
                                                                        <Presentation className="h-4 w-4 mx-auto mb-1 text-purple-500" />
                                                                        <p className="text-xs font-medium">{topic.presentation?.slides?.length || 0}</p>
                                                                        <p className="text-xs text-muted-foreground">Slides</p>
                                                                    </div>
                                                                    <div className="p-2 rounded-lg bg-muted/50 text-center">
                                                                        <HelpCircle className="h-4 w-4 mx-auto mb-1 text-amber-500" />
                                                                        <p className="text-xs font-medium">{topic.assessment?.questions?.length || 0}</p>
                                                                        <p className="text-xs text-muted-foreground">Questions</p>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* View Full Details Button */}
                                                                <Button 
                                                                    variant="outline" 
                                                                    className="w-full gap-2"
                                                                    onClick={() => openTopicDetail(topic)}
                                                                    data-testid={`view-topic-${topic.id}`}
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                    View Full Content
                                                                </Button>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                ))}
                                                {getTopicsForModule(selectedModule).length === 0 && (
                                                    <div className="text-center py-8 text-muted-foreground">
                                                        <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                        <p className="text-sm">No topics in this module</p>
                                                    </div>
                                                )}
                                            </Accordion>
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="h-full min-h-[400px] flex items-center justify-center">
                                <CardContent className="text-center">
                                    <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                                    <p className="text-muted-foreground">Select a module to view its topics</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>

                {/* Topic Detail Dialog */}
                <Dialog open={topicDetailOpen} onOpenChange={setTopicDetailOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <BookOpen className="h-5 w-5 text-primary" />
                                {selectedTopic?.name}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedTopic?.description}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedTopic && (
                            <Tabs defaultValue="content" className="mt-4">
                                <TabsList className="grid w-full grid-cols-4">
                                    <TabsTrigger value="content" className="gap-2">
                                        <FileText className="h-4 w-4" />
                                        Content
                                    </TabsTrigger>
                                    <TabsTrigger value="slides" className="gap-2">
                                        <Presentation className="h-4 w-4" />
                                        Slides
                                    </TabsTrigger>
                                    <TabsTrigger value="assessment" className="gap-2">
                                        <HelpCircle className="h-4 w-4" />
                                        Assessment
                                    </TabsTrigger>
                                    <TabsTrigger value="exercises" className="gap-2">
                                        <Target className="h-4 w-4" />
                                        Exercises
                                    </TabsTrigger>
                                </TabsList>
                                
                                <ScrollArea className="h-[500px] mt-4 pr-4">
                                    {/* Content Tab */}
                                    <TabsContent value="content" className="space-y-6 mt-0">
                                        {/* Introduction */}
                                        {selectedTopic.introduction && (
                                            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                                    <Lightbulb className="h-4 w-4 text-primary" />
                                                    Introduction
                                                </h4>
                                                <p className="text-sm text-muted-foreground leading-relaxed">
                                                    {selectedTopic.introduction}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Learning Objectives */}
                                        {selectedTopic.learning_objectives?.length > 0 && (
                                            <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                                                <h4 className="font-medium mb-3 flex items-center gap-2 text-emerald-600">
                                                    <Target className="h-4 w-4" />
                                                    Learning Objectives
                                                </h4>
                                                <ul className="space-y-2">
                                                    {selectedTopic.learning_objectives.map((obj, i) => (
                                                        <li key={i} className="text-sm flex items-start gap-2">
                                                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                            {obj}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {/* Sections */}
                                        {selectedTopic.sections?.length > 0 && (
                                            <div className="space-y-4">
                                                <h4 className="font-medium flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-blue-500" />
                                                    Content Sections
                                                </h4>
                                                {selectedTopic.sections.map((section, idx) => (
                                                    <Card key={idx} className="overflow-hidden">
                                                        <CardHeader className="bg-muted/30 py-3">
                                                            <CardTitle className="text-base flex items-center gap-2">
                                                                <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">
                                                                    {section.section_number || idx + 1}
                                                                </span>
                                                                {section.title}
                                                            </CardTitle>
                                                        </CardHeader>
                                                        <CardContent className="pt-4 space-y-4">
                                                            <p className="text-sm leading-relaxed">
                                                                {section.content}
                                                            </p>
                                                            
                                                            {section.key_points?.length > 0 && (
                                                                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                                                    <h5 className="text-sm font-medium mb-2 text-amber-600 flex items-center gap-1">
                                                                        <Lightbulb className="h-3 w-3" />
                                                                        Key Points
                                                                    </h5>
                                                                    <ul className="space-y-1">
                                                                        {section.key_points.map((point, i) => (
                                                                            <li key={i} className="text-xs flex items-start gap-2">
                                                                                <span className="text-amber-500">•</span>
                                                                                {point}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            
                                                            {section.examples?.length > 0 && (
                                                                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                                                    <h5 className="text-sm font-medium mb-2 text-blue-600">Examples</h5>
                                                                    {section.examples.map((ex, i) => (
                                                                        <div key={i} className="text-xs space-y-1">
                                                                            <p className="font-medium">{ex.title}</p>
                                                                            <p className="text-muted-foreground">{ex.scenario}</p>
                                                                            {ex.walkthrough && (
                                                                                <p className="text-muted-foreground italic">{ex.walkthrough}</p>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {section.common_mistakes?.length > 0 && (
                                                                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                                                                    <h5 className="text-sm font-medium mb-2 text-red-600 flex items-center gap-1">
                                                                        <AlertCircle className="h-3 w-3" />
                                                                        Common Mistakes
                                                                    </h5>
                                                                    <ul className="space-y-1">
                                                                        {section.common_mistakes.map((mistake, i) => (
                                                                            <li key={i} className="text-xs flex items-start gap-2">
                                                                                <span className="text-red-500">!</span>
                                                                                {mistake}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            
                                                            {section.pro_tips?.length > 0 && (
                                                                <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                                                                    <h5 className="text-sm font-medium mb-2 text-purple-600">Pro Tips</h5>
                                                                    <ul className="space-y-1">
                                                                        {section.pro_tips.map((tip, i) => (
                                                                            <li key={i} className="text-xs flex items-start gap-2">
                                                                                <span className="text-purple-500">★</span>
                                                                                {tip}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Summary */}
                                        {selectedTopic.summary && (
                                            <div className="p-4 rounded-xl bg-muted/50 border">
                                                <h4 className="font-medium mb-2">Summary</h4>
                                                <p className="text-sm text-muted-foreground">{selectedTopic.summary}</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                    
                                    {/* Slides Tab */}
                                    <TabsContent value="slides" className="space-y-4 mt-0">
                                        {selectedTopic.presentation?.slides?.length > 0 ? (
                                            <>
                                                {/* Present Slides Button */}
                                                <div className="flex justify-end">
                                                    <Button 
                                                        onClick={() => {
                                                            setSlideViewerData({
                                                                slides: selectedTopic.presentation?.slides || selectedTopic.slides || [],
                                                                title: selectedTopic.name
                                                            });
                                                            setSlideViewerOpen(true);
                                                        }}
                                                        className="gap-2"
                                                        data-testid="present-slides-btn"
                                                    >
                                                        <MonitorPlay className="h-4 w-4" />
                                                        Present Slides
                                                    </Button>
                                                </div>
                                                
                                                {/* Slide cards */}
                                                {selectedTopic.presentation.slides.map((slide, idx) => (
                                                    <Card key={idx} className="overflow-hidden">
                                                        <CardHeader className={`py-3 ${
                                                            slide.type === 'title' ? 'bg-gradient-to-r from-primary/10 to-secondary/10' :
                                                            slide.type === 'objectives' ? 'bg-emerald-500/10' : 'bg-sky-500/10'
                                                        }`}>
                                                            <div className="flex items-center justify-between">
                                                                <CardTitle className="text-base">{slide.title}</CardTitle>
                                                                <Badge variant="outline">Slide {slide.slide_number || idx + 1}</Badge>
                                                            </div>
                                                            {slide.subtitle && (
                                                                <CardDescription>{slide.subtitle}</CardDescription>
                                                            )}
                                                        </CardHeader>
                                                        {slide.bullet_points?.length > 0 && (
                                                            <CardContent className="pt-4">
                                                                <ul className="space-y-2">
                                                                    {slide.bullet_points.map((point, i) => (
                                                                        <li key={i} className="text-sm flex items-start gap-2">
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                                                                            {point}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </CardContent>
                                                        )}
                                                    </Card>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="text-center py-12 text-muted-foreground">
                                                <Presentation className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                                <p>No slides available for this topic</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                    
                                    {/* Assessment Tab */}
                                    <TabsContent value="assessment" className="space-y-4 mt-0">
                                        {selectedTopic.assessment?.questions?.length > 0 ? (
                                            <>
                                                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 mb-4">
                                                    <p className="text-sm">
                                                        <strong>Passing Score:</strong> {selectedTopic.assessment.passing_score || 70}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {selectedTopic.assessment.questions.length} questions total
                                                    </p>
                                                </div>
                                                {selectedTopic.assessment.questions.map((q, idx) => (
                                                    <Card key={idx}>
                                                        <CardContent className="pt-4">
                                                            <div className="flex items-start gap-3">
                                                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center shrink-0">
                                                                    {q.id || idx + 1}
                                                                </span>
                                                                <div className="flex-1 space-y-3">
                                                                    <p className="font-medium">{q.question}</p>
                                                                    <Badge variant="secondary" className="text-xs">
                                                                        {q.type === 'mcq' ? 'Multiple Choice' : 'True/False'}
                                                                    </Badge>
                                                                    {q.options && (
                                                                        <div className="space-y-2 mt-2">
                                                                            {q.options.map((opt, i) => (
                                                                                <div 
                                                                                    key={i} 
                                                                                    className={`p-2 rounded-lg text-sm ${
                                                                                        opt === q.correct_answer 
                                                                                            ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                                                                            : 'bg-muted/50'
                                                                                    }`}
                                                                                >
                                                                                    {opt === q.correct_answer && (
                                                                                        <CheckCircle className="h-3 w-3 text-emerald-500 inline mr-2" />
                                                                                    )}
                                                                                    {opt}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {q.type === 'true_false' && (
                                                                        <p className="text-sm">
                                                                            <strong>Answer:</strong> {q.correct_answer ? 'True' : 'False'}
                                                                        </p>
                                                                    )}
                                                                    {q.explanation && (
                                                                        <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-xs">
                                                                            <strong>Explanation:</strong> {q.explanation}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="text-center py-12 text-muted-foreground">
                                                <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                                <p>No assessment questions available</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                    
                                    {/* Exercises Tab */}
                                    <TabsContent value="exercises" className="space-y-4 mt-0">
                                        {selectedTopic.practical_exercises?.length > 0 ? (
                                            selectedTopic.practical_exercises.map((ex, idx) => (
                                                <Card key={idx}>
                                                    <CardHeader>
                                                        <CardTitle className="text-base flex items-center gap-2">
                                                            <Target className="h-4 w-4 text-primary" />
                                                            {ex.title}
                                                        </CardTitle>
                                                        <CardDescription>{ex.description}</CardDescription>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {ex.instructions?.length > 0 && (
                                                            <div>
                                                                <h5 className="text-sm font-medium mb-2">Instructions</h5>
                                                                <ol className="list-decimal list-inside space-y-1">
                                                                    {ex.instructions.map((inst, i) => (
                                                                        <li key={i} className="text-sm text-muted-foreground">{inst}</li>
                                                                    ))}
                                                                </ol>
                                                            </div>
                                                        )}
                                                        {ex.expected_outcome && (
                                                            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                                                <h5 className="text-sm font-medium mb-1 text-emerald-600">Expected Outcome</h5>
                                                                <p className="text-sm text-muted-foreground">{ex.expected_outcome}</p>
                                                            </div>
                                                        )}
                                                        {ex.hints?.length > 0 && (
                                                            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                                                <h5 className="text-sm font-medium mb-1 text-amber-600">Hints</h5>
                                                                <ul className="space-y-1">
                                                                    {ex.hints.map((hint, i) => (
                                                                        <li key={i} className="text-sm text-muted-foreground">💡 {hint}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))
                                        ) : (
                                            <div className="text-center py-12 text-muted-foreground">
                                                <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                                <p>No practical exercises available</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                </ScrollArea>
                            </Tabs>
                        )}
                    </DialogContent>
                </Dialog>
                
                {/* Slide Viewer */}
                <SlideViewer
                    slides={slideViewerData.slides}
                    title={slideViewerData.title}
                    open={slideViewerOpen}
                    onOpenChange={setSlideViewerOpen}
                    theme="professional"
                />
            </div>
        </MainLayout>
    );
};

export default Modules;
